import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { logPageEntry, logPageExit, logPageExitBeacon } from '../services/activityTrackingService';
import { getCookieConsent } from '../utils/cookieConsent';

// Generate a stable session ID per browser tab
const SESSION_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Paths we don't want to track
const IGNORED_PATHS = ['/login', '/reset-password'];

// Minimum time (ms) a user must spend on a page before we record it.
// Filters out accidental clicks / rapid back-navigation — reduces noise and write volume.
const MIN_DWELL_MS = 3000;

/**
 * Hook that automatically tracks page visits and time spent.
 * Should be placed once in the app layout so it runs on every route change.
 * All network calls are fire-and-forget to avoid blocking navigation/rendering.
 *
 * Performance safeguards:
 *  - Skips Super Admins entirely (also enforced server-side)
 *  - MIN_DWELL_MS debounce: cancels insert if user leaves within 3 s
 *  - Passes care_home_id for efficient per-care-home DB queries
 *  - Beacon / keepalive for unload events (non-blocking main thread)
 */
const useActivityTracker = () => {
  const location = useLocation();
  const { user, isSuperAdmin, careHomeId } = useAuth();
  const currentLogIdRef = useRef(null);
  const currentPathRef = useRef(null);
  // Timer ref for the MIN_DWELL_MS guard
  const dwellTimerRef = useRef(null);
  // Pending entry data while we wait for MIN_DWELL_MS
  const pendingEntryRef = useRef(null);

  // Memoized exit handler — non-blocking
  const closeCurrentLog = useCallback(() => {
    // Cancel any pending insert that hasn't been committed yet
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
      pendingEntryRef.current = null;
    }
    if (currentLogIdRef.current) {
      const id = currentLogIdRef.current;
      currentLogIdRef.current = null;
      // Fire-and-forget — don't await
      logPageExit(id);
    }
  }, []);

  useEffect(() => {
    if (!user?.id || isSuperAdmin) return;
    const consent = getCookieConsent();
    if (!consent?.analytics) return;
    const path = location.pathname;

    // Don't track ignored paths
    if (IGNORED_PATHS.some(p => path.startsWith(p))) return;

    // If the path hasn't actually changed, skip
    if (currentPathRef.current === path) return;

    // Close the previous page's log entry (fire-and-forget)
    closeCurrentLog();

    currentPathRef.current = path;

    // Delay the insert by MIN_DWELL_MS — if the user navigates away before
    // the timer fires we cancel the insert entirely, saving a wasted DB write.
    pendingEntryRef.current = { userId: user.id, sessionId: SESSION_ID, path, careHomeId };
    dwellTimerRef.current = setTimeout(() => {
      dwellTimerRef.current = null;
      const entry = pendingEntryRef.current;
      pendingEntryRef.current = null;
      if (!entry) return;
      logPageEntry(entry.userId, entry.sessionId, entry.path, entry.careHomeId).then((logId) => {
        currentLogIdRef.current = logId;
      });
    }, MIN_DWELL_MS);

    return () => {
      if (dwellTimerRef.current) {
        clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = null;
      }
    };
  }, [location.pathname, user?.id, isSuperAdmin, careHomeId, closeCurrentLog]);

  // Handle page unload / tab close
  useEffect(() => {
    if (!user?.id || isSuperAdmin) return;

    const handleUnload = () => {
      // Cancel any pending dwell timer — nothing to close yet
      if (dwellTimerRef.current) {
        clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = null;
      }
      if (currentLogIdRef.current) {
        logPageExitBeacon(currentLogIdRef.current);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      closeCurrentLog();
    };
  }, [user?.id, isSuperAdmin, closeCurrentLog]);
};

export default useActivityTracker;
